import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowUpRight, RotateCcw } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { CALENDLY_URL, TIERS, PRICING_FAQS, QUIZ, recommendTier, COMPLEXITY_NOTES } from "../data/content";

function TierCard({ tier, recommended }) {
  return (
    <div
      className={`relative flex flex-col h-full bg-white rounded-2xl p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
        tier.highlight ? "border-green" : "border-rule"
      }`}
      data-testid={`tier-card-${tier.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {recommended && (
        <span className="absolute -top-3 left-8 bg-amber text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full" data-testid="tier-recommended-badge">
          Best fit for you
        </span>
      )}
      {tier.highlight && !recommended && (
        <span className="absolute -top-3 left-8 bg-green text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full">
          Most popular
        </span>
      )}
      <h3 className="font-serif text-2xl text-ink mb-2">{tier.name}</h3>
      <p className="font-serif text-4xl text-green mb-4" data-testid={`tier-price-${tier.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{tier.price}</p>
      <p className="text-xs text-ink-soft leading-relaxed mb-8 pb-8 border-b border-rule">Best for: {tier.bestFor}</p>
      <ul className="space-y-3 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-ink-mid">
            <Check size={15} className="text-green mt-0.5 flex-none" /> {f}
          </li>
        ))}
      </ul>
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`tier-cta-${tier.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={`mt-10 inline-flex items-center justify-center gap-2 text-sm font-medium px-6 py-3.5 rounded-sm transition-colors duration-200 ${
          tier.highlight ? "bg-green text-white hover:bg-green-mid" : "border border-rule text-ink hover:bg-green-lt"
        }`}
      >
        Talk to us <ArrowUpRight size={14} />
      </a>
    </div>
  );
}

function Quiz({ onResult }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const answer = (idx) => {
    const next = [...answers, idx];
    setAnswers(next);
    if (step < QUIZ.length - 1) {
      setStep(step + 1);
    } else {
      onResult(recommendTier(next));
    }
  };

  const reset = () => { setStep(0); setAnswers([]); onResult(null); };

  return (
    <div className="bg-white rounded-2xl p-8 lg:p-14" data-testid="pricing-quiz">
      <div className="flex items-center justify-between mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-soft">
          Question {step + 1} of {QUIZ.length}
        </p>
        <div className="flex gap-1.5">
          {QUIZ.map((_, i) => (
            <span key={i} className={`h-1 w-8 rounded-full transition-colors duration-300 ${i <= step ? "bg-green" : "bg-rule"}`} />
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
          <h3 className="font-serif text-2xl lg:text-3xl text-ink mb-8" data-testid="quiz-question">{QUIZ[step].q}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QUIZ[step].options.map((opt, idx) => (
              <button
                key={opt}
                onClick={() => answer(idx)}
                data-testid={`quiz-option-${idx}`}
                className="text-left rounded-xl bg-paper px-5 py-4 text-sm text-ink-mid hover:border-green hover:bg-green-lt/50 hover:text-ink transition-colors duration-200"
              >
                {opt}
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      {step > 0 && (
        <button onClick={reset} className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-ink-soft hover:text-green transition-colors duration-200" data-testid="quiz-reset-btn">
          <RotateCcw size={12} /> Start over
        </button>
      )}
    </div>
  );
}

export default function Pricing() {
  const [result, setResult] = useState(null);

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: PRICING_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => document.getElementById("faq-schema")?.remove();
  }, []);

  return (
    <main className="pt-32 pb-16" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="font-mono text-lg uppercase tracking-[0.25em] text-ink-mid mb-5">Transparent pricing</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-ink mb-8">
          <RevealLine animateOnLoad>Simple tiers.</RevealLine>
          <RevealLine animateOnLoad delay={0.12}><span className="italic text-green">Right for your stage.</span></RevealLine>
        </h1>
        <FadeUp className="max-w-xl text-ink-mid leading-relaxed mb-20">
          We don't believe in one-size-fits-all retainers. Answer four quick questions and we'll show you which tier fits — and exactly why.
        </FadeUp>

        <FadeUp className="mb-24">
          <Quiz onResult={(r) => { setResult(r); if (r !== null) setTimeout(() => document.getElementById("tiers")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400); }} />
        </FadeUp>

        <div id="tiers" className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-14 scroll-mt-28">
          {TIERS.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.1} className="h-full">
              <TierCard tier={t} recommended={result === i} />
            </FadeUp>
          ))}
        </div>

        <FadeUp className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" >
          {COMPLEXITY_NOTES.map((n) => (
            <div key={n.label} className="bg-white rounded-2xl p-8" data-testid={`complexity-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber mb-3">{n.label}</p>
              <p className="text-sm text-ink-mid leading-relaxed">{n.text}</p>
            </div>
          ))}
        </FadeUp>

        <div className="max-w-3xl">
          <h2 className="font-serif text-4xl tracking-tight text-ink mb-10">
            <RevealLine>Frequently asked.</RevealLine>
          </h2>
          <FadeUp>
            <Accordion type="single" collapsible className="border-t border-rule" data-testid="pricing-faq">
              {PRICING_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-rule">
                  <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-serif text-lg text-ink hover:text-green hover:no-underline py-6 transition-colors duration-200">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-ink-mid leading-relaxed pb-6" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
